import type { HotelProductDTO } from '@/shared/model';
import {
  BeachLine,
  BeachType,
  ChildService,
  DistanceLift,
  HotelFacility,
  HotelFeature,
  RenovationType,
  RestType,
  WaterActivity,
} from './types';

// Сколько лет открытие/реновация считаются «свежими» для постфильтров.
const RENOVATION_FRESHNESS_YEARS = 5;

// До скольких метров отель считается близким к пляжу, подъёмнику или центру.
const NEARBY_DISTANCE_METERS = 500;

const NEARBY_DISTANCE_CENTER_METERS = 1000;

// Атрибут собственного пляжа отеля.
const BEACH_OWNER_ATTRIBUTE = 134;

// Отель НЕ принимает одиноких мужчин, когда у него есть этот атрибут, — признак считается инверсией.
const LONELY_MAN_FORBIDDEN_ATTRIBUTE = 250;

// Тип отдыха «с детьми» из `types`: он же единственный признак детей в правиле удобного входа в море.
const KIDS_REST_TYPE = 5;

// Тип отдыха «для пар».
const COUPLES_REST_TYPE = 8;

// Атрибуты пляжа, при которых вход в море перестаёт считаться удобным даже у песчаного пляжа.
const UNCOMFORTABLE_SEA_ENTRY_ATTRIBUTES = [138, 139, 140];

// Каждый из этих сервисов сам по себе делает отель подходящим для отдыха с детьми.
const KIDS_REST_DEFINING_SERVICES = [ChildService.Playroom, ChildService.Animators, ChildService.Clubs];

// А эти — только совокупностью: поодиночке они на выбор отеля для отдыха с детьми не влияют.
const KIDS_REST_COUNTED_SERVICES = [ChildService.Pool, ChildService.Clubs, ChildService.Bed, ChildService.Menu];
const KIDS_REST_COUNTED_SERVICES_THRESHOLD = 2;

const DISTANCE_LIFT_BUS_ATTRIBUTES = [215, 214, 238];
const DISTANCE_LIFT_SKI_IN_ATTRIBUTE = 225;
const DISTANCE_LIFT_WALKING_ATTRIBUTE = 213;

const hasAnyAttribute = (attributeIds: Set<number>, wantedIds: number[]): boolean =>
  wantedIds.some((attributeId) => attributeIds.has(attributeId));

// Дистанции в DTO nullable: null и 0 означают «нет данных», а не близость.
const isDistanceBelow = (distance: number | null, limitMeters: number): boolean =>
  distance !== null && distance > 0 && distance < limitMeters;

// У подъёмника граница включающая, а у пляжа и центра строгая — так считает десктопная выдача.
const isDistanceAtMost = (distance: number | null, limitMeters: number): boolean =>
  distance !== null && distance > 0 && distance <= limitMeters;

export default class HotelProduct {
  readonly id: number;
  readonly name: string;
  readonly country: number;
  readonly resort: number;
  readonly hotelCategory: number;
  readonly attributes: number[];
  readonly types: number[];
  readonly coords: HotelProductDTO['coords'];
  readonly distances: HotelProductDTO['distances'];
  readonly cover: string;
  readonly photos: string[];
  readonly rating: number | null;
  readonly reviewsNumber: number;
  readonly ordersNumber: number;
  readonly beachLine: number | null;
  readonly builtDate: number | null;
  readonly renovationDate: number | null;
  readonly placement: string | null;
  readonly childServices: ChildService[];
  readonly restTypes: RestType[];
  readonly beachType: BeachType | null;
  readonly beachLineType: BeachLine | null;
  readonly renovationTypes: RenovationType[];
  readonly distanceLift: DistanceLift | null;
  readonly features: HotelFeature[];
  readonly facilities: HotelFacility[];
  readonly waterActivities: WaterActivity[];
  readonly beachOwner: boolean;
  readonly hasConvenientSeaEntry: boolean;

  constructor(data: HotelProductDTO) {
    this.id = data.id;
    this.name = data.name;
    this.country = data.country;
    this.resort = data.resort;
    this.hotelCategory = data.hotelCategory;
    this.attributes = data.attributes;
    this.types = data.types;
    this.coords = data.coords;
    this.distances = data.distances;
    this.cover = data.cover;
    this.photos = data.photos;
    this.rating = data.rating;
    this.reviewsNumber = data.reviewsNumber;
    this.ordersNumber = data.ordersNumber;
    this.beachLine = data.beachLine;
    this.builtDate = data.builtDate;
    this.renovationDate = data.renovationDate;
    this.placement = data.placement;
    this.childServices = this.getChildServices();
    this.restTypes = this.getRestTypes();
    this.beachType = this.getBeachType();
    this.beachLineType = this.getBeachLineType();
    this.renovationTypes = this.getRenovationTypes();
    this.distanceLift = this.getDistanceLift();
    this.features = this.getFeatures();
    this.facilities = this.getFacilities();
    this.waterActivities = this.getWaterActivities();
    this.beachOwner = new Set(data.attributes).has(BEACH_OWNER_ATTRIBUTE);
    this.hasConvenientSeaEntry = this.getHasConvenientSeaEntry();
  }

  private getChildServices(): ChildService[] {
    const attributeIds = new Set(this.attributes);
    const result: ChildService[] = [];

    if (attributeIds.has(229)) result.push(ChildService.Animators);
    if (attributeIds.has(111)) result.push(ChildService.Pool);
    if (attributeIds.has(163)) result.push(ChildService.Menu);
    if (attributeIds.has(109)) result.push(ChildService.Playroom);
    if (attributeIds.has(112)) result.push(ChildService.Clubs);
    if (attributeIds.has(114) || attributeIds.has(246) || attributeIds.has(247)) result.push(ChildService.Bed);
    if (attributeIds.has(113) || attributeIds.has(110)) result.push(ChildService.Nanny);

    return result;
  }

  private getRestTypes(): RestType[] {
    if (!this.types) return [];

    const typeIds = new Set(this.types);
    const result: RestType[] = [];

    if (typeIds.has(1)) result.push(RestType.Beach);
    if (typeIds.has(2)) result.push(RestType.City);
    if (typeIds.has(3)) result.push(RestType.Mountain);
    if (typeIds.has(4)) result.push(RestType.Wellness);
    if (typeIds.has(11)) result.push(RestType.Eco);

    return result;
  }

  private getBeachType(): BeachType | null {
    const attributeIds = new Set(this.attributes);

    if (attributeIds.has(136)) return BeachType.SandBeach;
    if (attributeIds.has(137)) return BeachType.PebbleBeach;
    if (attributeIds.has(211)) return BeachType.SandPebbleBeach;

    return null;
  }

  private getBeachLineType(): BeachLine | null {
    if (this.beachLine === 1) return BeachLine.First;
    if (this.beachLine === 2) return BeachLine.Second;
    if (this.beachLine === 3) return BeachLine.Third;

    return null;
  }

  private getRenovationTypes(): RenovationType[] {
    const { builtDate, renovationDate } = this;
    if (!builtDate && !renovationDate) return [];

    const currentYear = new Date().getFullYear();
    const result: RenovationType[] = [];

    if (builtDate && currentYear - builtDate <= RENOVATION_FRESHNESS_YEARS) {
      result.push(RenovationType.RecentlyOpened);
    }
    if (renovationDate && currentYear - renovationDate <= RENOVATION_FRESHNESS_YEARS) {
      result.push(RenovationType.FreshRenovation);
    }

    return result;
  }

  private getDistanceLift(): DistanceLift | null {
    const attributeIds = new Set(this.attributes);

    if (hasAnyAttribute(attributeIds, DISTANCE_LIFT_BUS_ATTRIBUTES)) return DistanceLift.Bus;
    if (attributeIds.has(DISTANCE_LIFT_SKI_IN_ATTRIBUTE)) return DistanceLift.SkiIn;
    if (attributeIds.has(DISTANCE_LIFT_WALKING_ATTRIBUTE)) return DistanceLift.Walking;

    return null;
  }

  private getFeatures(): HotelFeature[] {
    const attributeIds = new Set(this.attributes);
    const typeIds = new Set(this.types);
    // Поиск отдаёт distances необязательным и неполным (в ответе бывает нет ни объекта, ни отдельных ключей),
    // а DTO не валидируется — без дефолтов один такой отель ронял бы разбор всей порции результатов.
    const distances: Partial<HotelProductDTO['distances']> = this.distances ?? {};
    const { beach = null, beachMax = null, center = null, lift = null } = distances;
    const result: HotelFeature[] = [];

    const withKids = this.hasKidsRest(attributeIds, typeIds);
    const adultsOnly = attributeIds.has(217);
    // «Только для взрослых» и «отдых с детьми» противоречат друг другу — при совпадении гасим оба признака.
    const kidsAndAdultsConflict = withKids && adultsOnly;

    // Кондиционер: бесплатно (83), платно (212), без данных о стоимости (233).
    if (hasAnyAttribute(attributeIds, [83, 212, 233])) {
      result.push(HotelFeature.AirConditioner);
    }
    // Интернет в номере (74, 226, 234) и на территории (88, 227, 237) — в любом варианте оплаты.
    if (hasAnyAttribute(attributeIds, [74, 88, 226, 227, 234, 237])) {
      result.push(HotelFeature.Wifi);
    }
    if (
      isDistanceBelow(beachMax, NEARBY_DISTANCE_METERS) ||
      isDistanceBelow(beach, NEARBY_DISTANCE_METERS) ||
      // placement приходит строкой: пустая строка и null дают 0 и признаком близости не становятся.
      Number(this.placement) > 0
    ) {
      result.push(HotelFeature.NearTheBeach);
    }
    if (hasAnyAttribute(attributeIds, [213, 225]) || isDistanceAtMost(lift, NEARBY_DISTANCE_METERS)) {
      result.push(HotelFeature.NearTheLift);
    }
    if (isDistanceBelow(center, NEARBY_DISTANCE_CENTER_METERS)) {
      result.push(HotelFeature.NearTheCenter);
    }
    if (withKids && !kidsAndAdultsConflict) result.push(HotelFeature.WithKids);
    if (typeIds.has(8) || hasAnyAttribute(attributeIds, [169, 220, 217])) {
      result.push(HotelFeature.ForCouples);
    }
    if (adultsOnly && !kidsAndAdultsConflict) result.push(HotelFeature.AdultsOnly);
    if (this.restTypes.includes(RestType.Wellness)) result.push(HotelFeature.Wellness);

    return result;
  }

  private getFacilities(): HotelFacility[] {
    const attributeIds = new Set(this.attributes);
    const typeIds = new Set(this.types);
    const result: HotelFacility[] = [];

    if (!attributeIds.has(LONELY_MAN_FORBIDDEN_ATTRIBUTE)) result.push(HotelFacility.LonelyMan);
    // Кондиционер: бесплатно (83), платно (212), без данных о стоимости (233).
    if (hasAnyAttribute(attributeIds, [83, 212, 233])) result.push(HotelFacility.AirConditioner);
    // Интернет в номере (74, 226, 234) и на территории (88, 227, 237) — в любом варианте оплаты.
    if (hasAnyAttribute(attributeIds, [74, 88, 226, 227, 234, 237])) result.push(HotelFacility.Wifi);
    if (attributeIds.has(147)) result.push(HotelFacility.SpaAndHealth);
    if (attributeIds.has(264)) result.push(HotelFacility.BayLocated);
    // В отличие от `features`, «только для взрослых» и «с детьми» здесь друг друга не гасят: удобства
    // описывают, что в отеле есть, а не кому он подходит, — поэтому отель может попасть в оба фильтра.
    if (attributeIds.has(217)) result.push(HotelFacility.AdultsOnly);
    // Игровая комната (109), аниматоры (229), детский клуб (112) — любого из них достаточно.
    if (typeIds.has(KIDS_REST_TYPE) || hasAnyAttribute(attributeIds, [109, 229, 112])) {
      result.push(HotelFacility.WithKids);
    }
    if (typeIds.has(COUPLES_REST_TYPE) || hasAnyAttribute(attributeIds, [169, 220, 217])) {
      result.push(HotelFacility.ForCouples);
    }
    if (attributeIds.has(166)) result.push(HotelFacility.WithAnimals);
    if (hasAnyAttribute(attributeIds, [69, 263])) result.push(HotelFacility.Parking);
    if (hasAnyAttribute(attributeIds, [148, 149])) result.push(HotelFacility.SaunaBathHammam);
    if (hasAnyAttribute(attributeIds, [185, 64])) result.push(HotelFacility.Jacuzzi);

    return result;
  }

  private getWaterActivities(): WaterActivity[] {
    const attributeIds = new Set(this.attributes);
    const result: WaterActivity[] = [];

    if (attributeIds.has(60)) result.push(WaterActivity.OpenPool);
    if (attributeIds.has(59)) result.push(WaterActivity.IndoorPool);
    if (attributeIds.has(174)) result.push(WaterActivity.HeatedPool);
    if (attributeIds.has(61)) result.push(WaterActivity.Aquapark);
    if (attributeIds.has(173)) result.push(WaterActivity.SeaWaterPool);
    if (attributeIds.has(175)) result.push(WaterActivity.WaterSlides);

    return result;
  }

  /**
   * Удобный вход в море: песчаный пляж (136) без примеси гальки у отеля для отдыха с детьми.
   *
   * Детей здесь определяет только тип отдыха 5, без «определяющих» детских сервисов: так считает
   * десктопная выдача, где это правило стоит до того, как признак отдыха с детьми дополняется сервисами.
   */
  private getHasConvenientSeaEntry(): boolean {
    const attributeIds = new Set(this.attributes);
    const typeIds = new Set(this.types);

    return (
      attributeIds.has(136) &&
      typeIds.has(KIDS_REST_TYPE) &&
      !attributeIds.has(137) &&
      !attributeIds.has(211) &&
      !hasAnyAttribute(attributeIds, UNCOMFORTABLE_SEA_ENTRY_ATTRIBUTES)
    );
  }

  private hasKidsRest(attributeIds: Set<number>, typeIds: Set<number>): boolean {
    if (typeIds.has(5)) return true;
    if (KIDS_REST_DEFINING_SERVICES.some((service) => this.childServices.includes(service))) return true;

    const countedServices = KIDS_REST_COUNTED_SERVICES.filter((service) => this.childServices.includes(service)).length;
    // Водные горки (175) в детские сервисы карточки не попадают, но в этом наборе признаков участвуют.
    const waterSlides = attributeIds.has(175) ? 1 : 0;

    return countedServices + waterSlides > KIDS_REST_COUNTED_SERVICES_THRESHOLD;
  }
}
